<template>
    <div class="layout-pods orders">
        <header class="layout-pods-header pg-header">
            <div></div>
        </header>
        <div class="row">
            <div class="col-12 col-md-12 col-lg-8 filter-elements">
                <div class="col-12 col-md-4 col-lg-3 pb-2 pr-2">                    
                    <client-list-dropdown
                      :value="selectedClient"
                      @on-client-selected="handleClientSelected"
                      @on-client-removed="handleClientRemoved"
                      @on-client-selection-updated="handleClientSelectionUpdated"
                    />
                </div>
                <div class="col-12 col-md-4 col-lg-3 pb-2 pr-2">    
                    <agency-list-dropdown
                      :client-ids="agenciesDropdownFilter"
                      :value="selectedAgencies"
                      :multiple="true"
                      @on-agency-selected="handleAgencySelected"
                      @on-agency-removed="handleAgencyRemoved"
                      :disabled="noClientSelected"
                    />
                </div>
                <div class="col-12 col-md-4 col-lg-3 pb-2 pr-2"> 
                    <user-role-list-dropdown
                      :value="selectedUserRoles"
                      :multiple="true"
                      @on-user-role-selected="handleUserRoleSelected"
                      @on-user-role-removed="handleUserRoleRemoved"
                      :disabled="noClientSelected"
                    />
                </div>
                <div class="col-12 col-md-4 col-lg-3 pb-2 pr-2"> 
                    <user-status-list-dropdown
                      :value="selectedUserStatus"
                      @on-user-status-selected="handleUserStatusSelected"
                      @on-user-status-removed="handleUserStatusRemoved"
                      :disabled="noClientSelected"
                    />
                </div>
                <!-- Search Bar as last filter -->
                <div class="col-12 col-md-4 col-lg-3 pb-2 pr-2">
                    <div class="input-group">
                        <input
                            type="search"
                            class="form-control"
                            placeholder="Search users by name, email, office..."
                            aria-label="Search"
                            v-model="searchText"
                            :disabled="noClientSelected"
                        >
                        <div class="input-group-append">
                            <span class="input-group-text"><i class="fa fa-search"></i></span>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-12 col-md-12 col-lg-4">
                <div class="d-flex justify-content-end align-items-center">
                    <button type="button" class="btn btn-primary mr-2" @click="openModal(null)" :disabled="canCreateUser === false">Create user</button>
                    <button type="button" @click="downloadCsv" class="btn btn-quaternary">Export CSV</button>
                </div>
            </div>
        </div>
        <br>
        <app-action-needed
            v-if="selectedClient === null"
            title="No client selected"
            description="Please select a client to get started."
        />

        <app-no-data-available
            v-if="selectedClient !== null && users.length === 0"
            title="No data available"
            description="There isn't any users available with the given criteria."
        />
        <table class="table table-bordered" v-if="users.length > 0">
            <thead>
                <tr>
                    <th>Full Name</th>
                    <th>Email</th>
                    <th>Office</th>
                    <th>Modified</th>
                    <th>Role</th>
                    <th>Active</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(user) in users" :key="user.id" class="action-row" @click="openModal(user)">
                    <td>{{ user.firstName }} {{ user.lastName }}</td>
                    <td>{{ user.email }}</td>
                    <td>
                        <template v-if="user.role.value == 'ROLE_CLIENT_ADMIN'">
                            {{ getClientName(user.selectedClient) }} Offices
                        </template>
                        <template v-else-if="user.role.value == 'ROLE_ADMIN'">
                            All Offices
                        </template>
                        <template v-else-if="user.permissions && user.permissions.data">
                            <template v-for="(permission, idx) in user.permissions.data">
                                {{ permission.agencyName }}
                                <template v-if="idx != user.permissions.data.length - 1">
                                    ,
                                </template>
                            </template>
                        </template>
                        <template v-else-if="user.permissions">
                            <template v-for="(permission, idx) in user.permissions">
                                {{ permission.agencyName }}
                                <template v-if="idx != user.permissions.length - 1">
                                    ,
                                </template>
                            </template>
                        </template>
                    </td>
                    <td>{{ user.updatedAt | formatDate('DD/MM/YYYY HH:mm') }}</td>
                    <td>{{ user.role.name }}</td>
                    <td>
                        <div class="c__checkbox">
                            <input type="checkbox" v-bind:checked="user.enabled" v-on:click.stop="changeUserStatus(user)"/>
                            <label>Active</label>
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>

        <div class="col-12 mt-4" v-if="users.length > 0 && hasMorePages">
            <div class="btn-container" style="text-align: center">
            <button
                class="btn-primary btn"
                @click="currentCursor = nextCursor"
            >
                Load more
            </button>
            </div>
        </div>
        <transition name="modal">
            <user-modal
                v-if="showModal"
                :user-id="userId"
                @closeModal="closeModal"
                :client-ids="selectedClient === null ? undefined : [selectedClient.id]"
            ></user-modal>
        </transition>
    </div>
</template>

<script>
    import UserModal from './UserModal';
    import UserServices from '../../services/users';
    import ClientPerimeters from '../../configs/Perimeters/clients';
    import UsersPerimeters from '../../configs/Perimeters/users';
    import { mapGetters } from 'vuex';
    import { refreshPageMixin } from '../../mixins/refreshPage';
    import ClientListDropdown, { ClientListAllOption } from "../../components/Clients/ClientListDropdown.vue"
    import AgencyListDropdown, { AgencyListAllOption } from "../../components/Agencies/AgencyListDropdown.vue"
    import { OrderDropdownFilterSelectedHelper } from '../../helpers/OrderDropdownFilterSelectedHelper';
    import UserRoleListDropdown, { UserRoleListAllOption } from './UserRoleListDropdown.vue';
    import UserStatusListDropdown, { UserStatusListAllOption } from './UserStatusListDropdown.vue';
    import { FilterBuilder } from '../../helpers/FilterBuilder';
    import AppActionNeeded from '../AppComponents/AppActionNeeded.vue';
    import AppNoDataAvailable from '../AppComponents/AppNoDataAvailable.vue';
import { StringToCapitalizedCase } from '../../helpers/StringToCapitalizedCase';

    export default {
        mixins: [refreshPageMixin],
        perimeters:[ClientPerimeters,UsersPerimeters],
        components: {
            UserModal,
            ClientListDropdown,
            AgencyListDropdown,
            UserRoleListDropdown,
            UserStatusListDropdown,
            AppActionNeeded,
            AppNoDataAvailable
        },
        data() {
            return {
                selectedClient: null,
                selectedAgencies: [],
                selectedUserRoles: [],
                selectedUserStatus: null,
                filterBuilder: new FilterBuilder(),
                currentCursor: 0,
                nextCursor: 0,
                hasMorePages: false,
                showModal: false,
                userId: null,
                users: [],
                clients: [],
                searchText: '',
                searchTimeout: null,
                isSearching: false,
            }
        },
        methods: {
            handleClientSelected(value) {
                this.filterBuilder.reset();
                if (value.id === ClientListAllOption.id) {
                    this.filterBuilder.removeFilter("client");
                } else {
                    this.filterBuilder.addFilter("client", [value.id]);
                }
                this.selectedClient = value;
                this.selectedAgencies = []; // Reset agencies when selecting client
            },
            handleClientRemoved(value) {
                this.clearFilters();
            },
            handleClientSelectionUpdated(value) {
                this.clients = value;
            },
            handleAgencySelected(value) {
                const agencies = OrderDropdownFilterSelectedHelper(
                    this.selectedAgencies,
                    value,
                    AgencyListAllOption
                );

                this.filterBuilder.updateFilter(
                    "agency", 
                    agencies
                    .filter(agency => agency.id !== AgencyListAllOption.id) // Remove "all"
                    .flatMap(agency => agency.id)
                );

                this.selectedAgencies = agencies;
            },
            handleAgencyRemoved(value) {
                const agencies = this.selectedAgencies.filter(agency => agency.id !== value.id);

                if (agencies.length === 0) {
                    this.filterBuilder.removeFilter("agency");
                } else {
                    this.filterBuilder.updateFilter("agency", agencies);
                }

                this.selectedAgencies = agencies;
            },
            handleUserRoleSelected(value) {
                const roles = OrderDropdownFilterSelectedHelper(
                    this.selectedUserRoles,
                    value,
                    UserRoleListAllOption
                );

                this.filterBuilder.updateFilter(
                    "role",
                    roles
                    .filter(role => role.id !== UserRoleListAllOption.id) // Remove "all"
                    .flatMap(role => role.id));

                this.selectedUserRoles = roles;
            },
            handleUserRoleRemoved(value) {
                const roles = this.selectedUserRoles.filter(role => role.id !== value.id);
                if (roles.length === 0) {
                    this.filterBuilder.removeFilter("role");
                } else {
                    this.filterBuilder.updateFilter("role", roles.flatMap(role => role.id));
                }

                this.selectedUserRoles = roles;
            },
            handleUserStatusSelected(value) {
                if (value.id === UserStatusListAllOption.id) {
                    this.filterBuilder.removeFilter("enabled");
                } else {
                    this.filterBuilder.addFilter("enabled", [value.id]);
                }

                this.selectedUserStatus = value;
            },
            handleUserStatusRemoved() {
                this.selectedUserStatus = null;
            },
            clearFilters() {
                this.filterBuilder.reset();
                const isAdmin = UsersPerimeters.isAdmin() || UsersPerimeters.isSuperAdmin();

                if (isAdmin) {
                    this.selectedClient = null;
                } else {
                    this.filterBuilder.addFilter("client", [this.selectedClient.id]);
                    this.selectedClient = null;
                }

                this.selectedAgencies = [];
                this.selectedUserRoles = [];
                this.selectedUserStatus = null;
                this.users = [];
            },
            getClientName(id) {
                let val = this.clients.find(client => client.id === id);
                if(val) return val.title;

                return null;
            },
            closeModal() {
                this.showModal = false;
                if (null !== this.selectedClient) {
                    this.getUsers('published');
                }
            },
            openModal(user) {
                this.userId = user ? user.id : null;
                this.showModal = true;
            },
            changeUserStatus(user) {
                user.enabled = !user.enabled;
                if (user.enabled) {
                    if(user.imageOriginal && user.imageOriginal.id) {
                        user.imageOriginal = user.imageOriginal.id;
                    }
                    const updateUserObject = {...user};
                    updateUserObject.role = updateUserObject.role.value;
                    UserServices.updateUser(updateUserObject).then(user => {
                    }).catch((errors) => {
                        this.errors = errors;
                    });
                } else {
                    UserServices.deleteUser(user.id).then(user => {
                    }).catch((errors) =>{
                        this.errors = errors;
                    })
                }
            },
            getUsers() {
                // Always use filterBuilder for all filters, including searchText
                const filters = Object.keys(this.filterBuilder.get()).length === 0 ? null : this.filterBuilder.get();
                UserServices.getUsers(
                    filters,
                    'permissions',
                    this.currentCursor
                ).then(response => {
                    this.users = this.currentCursor === 0 ? response.data : [...this.users, ...response.data];
                    const hasNext = response.meta.cursor && response.meta.cursor.hasOwnProperty("next");
                    this.hasMorePages = hasNext;
                    if (hasNext) {
                        this.nextCursor = response.meta.cursor.next.cursor;
                    }
                });
            },
            downloadCsv() {
                var today = new Date();
                var date = today.getFullYear()+'-'+(today.getMonth()+1)+'-'+today.getDate();
                const filters = Object.keys(this.filterBuilder.get()).length === 0 ? null : this.filterBuilder.get();
                UserServices.exportUserCsv(filters).then((blob) => {
                    if (blob.size > 0) {
                        let url = window.URL.createObjectURL(new Blob([blob]));
                        let link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', `Acquire_Users_CSV_${date}.csv`);
                        document.body.appendChild(link);
                        link.click();
                    }
                });
            },        
            /**
             * Handles loading the data based on what type of user role the
             * current user has. 
             */
            initData() {
                const isAdmin = UsersPerimeters.isAdmin() || UsersPerimeters.isSuperAdmin();
                if (isAdmin) return;

                this.filterBuilder.addFilter("client", [this.$store.getters.getUser.clientId]);

                setTimeout(() => {
                    this.selectedClient = {
                        id: this.$store.getters.getUser.clientId,
                        title: StringToCapitalizedCase(this.$store.getters.getUser.client),
                    }
                }, 250)

            }
        },
        computed: {
            ...mapGetters(['getUsersFilter']),
            agenciesDropdownFilter() {
                if (this.selectedClient === null) return null;
                if (this.selectedClient.id === ClientListAllOption.id) return null;

                return [this.selectedClient.id];
            },
            noClientSelected() {
                return this.selectedClient === null;
            },
            canCreateUser() {
                if (this.selectedClient === null) return false;
                if (this.selectedClient.id === ClientListAllOption.id) return false;

                return true;
            },
            watchedFilters() {
                return {
                    selectedClient: this.selectedClient,
                    selectedAgencies: this.selectedAgencies,
                    selectedUserRoles: this.selectedUserRoles,
                    selectedUserStatus: this.selectedUserStatus,
                }
            },
        },
        watch: {
            watchedFilters: {
                handler(value) {
                    if (this.selectedClient === null) return;
                    this.currentCursor = 0;
                    this.nextCursor = 0;
                    this.getUsers();
                }
            },
            currentCursor: {
                handler(value) {
                    if (value === 0) return;
                    this.getUsers();
                }
            },
            searchText: {
                handler(value) {
                    clearTimeout(this.searchTimeout);
                    this.currentCursor = 0;
                    this.nextCursor = 0;
                    if (!value) {
                        // Remove search filter if search is cleared
                        this.filterBuilder.removeFilter('search');
                        this.getUsers();
                    } else {
                        // Add or update search filter
                        this.filterBuilder.addFilter('search', value);
                        this.searchTimeout = setTimeout(() => {
                            this.getUsers();
                        }, 500);
                    }
                }
            }
        },
        mounted() {
            this.initData();
        }
    }
</script>

<style lang="css">
.users-search {
    margin-bottom: 0;
}
.users-search .form-control {
    border-radius: 4px;
    font-size: 1rem;
    padding: 0.5rem 1rem;
}
@media (max-width: 768px) {
    .users-search .form-control {
        font-size: 0.95rem;
        padding: 0.4rem 0.8rem;
    }
}
</style>
